import type { ReactNode } from "react";

import { cn } from "@/utils/utils";

export interface TabItem<T extends string> {
  value: T;
  label: ReactNode;
  /** A count beside the label. Zero still shows — "0" is information. */
  count?: number;
  disabled?: boolean;
}

/**
 * An underline tab strip.
 *
 * Underline rather than pills: pills read as buttons, and a row of buttons
 * where only one is filled makes the unselected ones look disabled. Arrow keys
 * move between tabs, as they do everywhere else that calls itself a tablist.
 */
export function Tabs<T extends string>({
  items,
  value,
  onChange,
  label,
  className,
}: {
  items: readonly TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Names the strip for a screen reader — "Member sections", say. */
  label: string;
  className?: string;
}) {
  const move = (delta: number) => {
    const usable = items.filter((item) => !item.disabled);
    const index = usable.findIndex((item) => item.value === value);
    const next = usable[(index + delta + usable.length) % usable.length];
    if (next) onChange(next.value);
  };

  return (
    // The rule and the scroller are separate elements, and the pull that sits
    // the tabs on top of the rule belongs to the scroller rather than to each
    // tab. On the tabs it was a pixel of vertical overflow, and `overflow-x:
    // auto` makes the other axis `auto` too, so the strip carried a vertical
    // scrollbar for a pixel nobody could scroll to.
    // `min-w-0` on the wrapper too: it is the flex or grid item now, and an
    // item that will not shrink makes the scroller inside it moot.
    <div className={cn("hairline-b min-w-0", className)}>
      <div
        role="tablist"
        aria-label={label}
        className="-mb-px flex min-w-0 gap-1 overflow-x-auto"
        onKeyDown={(event) => {
          if (event.key === "ArrowRight") move(1);
          else if (event.key === "ArrowLeft") move(-1);
          else return;
          event.preventDefault();
        }}
      >
        {items.map((item) => {
          const selected = item.value === value;
          return (
            <button
              key={item.value}
              type="button"
              role="tab"
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              disabled={item.disabled}
              onClick={() => onChange(item.value)}
              className={cn(
                "text-callout relative flex shrink-0 items-center gap-2 rounded-t-md px-3 pt-2 pb-2.5",
                "border-b-2 font-medium transition-colors duration-[var(--duration-fast)]",
                "disabled:pointer-events-none disabled:opacity-40",
                // Accent on both the rule and the word. The underline alone
                // names the tab you are on; the colour is what makes it
                // findable at a glance, and it is the same blue every other
                // active control in the app answers to.
                selected
                  ? "border-accent text-accent"
                  : "text-fg-muted hover:text-fg border-transparent",
              )}
            >
              {item.label}
              {item.count === undefined ? null : (
                <span
                  className={cn(
                    "text-footnote rounded-full px-1.5 py-px tabular-nums",
                    selected ? "bg-accent-soft text-accent" : "bg-surface-inset text-fg-muted",
                  )}
                >
                  {item.count.toLocaleString()}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * A segmented control: two or three mutually exclusive views of the same data,
 * where the choice is a lens rather than a destination.
 */
export function Segmented<T extends string>({
  items,
  value,
  onChange,
  label,
  className,
}: {
  items: readonly { value: T; label: ReactNode }[];
  value: T;
  onChange: (value: T) => void;
  label: string;
  className?: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(
        "bg-surface-inset inline-flex shrink-0 gap-0.5 rounded-[10px] p-0.5",
        className,
      )}
    >
      {items.map((item) => {
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(item.value)}
            className={cn(
              "text-subhead rounded-lg px-3 py-1.5 font-medium",
              "transition-[background-color,color,box-shadow] duration-[var(--duration-fast)]",
              selected
                ? "bg-surface-raised text-accent shadow-[var(--shadow-lift)]"
                : "text-fg-muted hover:text-fg",
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
